/* eslint-disable complexity */
import React from 'react';

import { UILoader } from '@futrli/ui-components';

import { CASH_FLOW_MAIN_CATEGORIES, CATEGORY_NAME_INDEXES, SIZES } from '../../../../core/constants';
import { ForecastTree, ForecastTreeNode, UKForecastLayoutItem, UKForecastLayoutItemDisplayType } from '../types';
import { ForecastUpdating } from '../../components/forecast-updating';
import { getTopLevelStaticCategoryTreeNode } from '../utils/forecast-tree';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { useOrganisationAccountNames } from '../hooks/useOrganisationAccountNames/useOrganisationAccountNames';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { useUKForecastGrid } from '../hooks/uk/useUKForecastGrid/useUKForecastGrid';

/**
 * Used on level 0 total nodes (grey ones), this ensures the node never has any children.
 * This solves the issue of the 'expand all' button showing child nodes on these non-expandable rows.
 */
const forceNoChildren = (nodes: ForecastTreeNode[]) => {
  if (nodes?.length) {
    return [
      {
        ...nodes[0],
        children: []
      }
    ];
  }
  return nodes;
};

const getLayout = (trees: Record<string, ForecastTree>) => {
  const datasource = 'cash_flow';
  const tree = trees[datasource];
  const layout: UKForecastLayoutItem[] = [];

  const findNode = (category: string) => {
    return getTopLevelStaticCategoryTreeNode(tree, category as CATEGORY_NAME_INDEXES);
  };

  // Cash flow nodes
  const startingBalanceNode = findNode(CASH_FLOW_MAIN_CATEGORIES.StartingBalance);
  const cashInNode = findNode('cash_in');
  const cashOutNode = findNode('cash_out');
  const netCashFlowNode = findNode('net_cash_flow');
  const closingBalanceNode = findNode(CASH_FLOW_MAIN_CATEGORIES.ClosingBalance);
  const cashFromBusinessActivitiesNode = findNode('cash_from_business_activities');
  const cashFromFinancingInvestingNode = findNode('cash_from_financing_and_investing');

  // P&L nodes
  const incomeNode = findNode(CATEGORY_NAME_INDEXES.income);
  const costOfSalesNode = findNode(CATEGORY_NAME_INDEXES.cost_of_sales);
  const expensesNode = findNode(CATEGORY_NAME_INDEXES.expenses);
  const otherExpensesNode = findNode(CATEGORY_NAME_INDEXES.other_expenses);
  const otherIncomeNode = findNode(CATEGORY_NAME_INDEXES.other_income);

  // Balance sheet nodes
  const fixedAssetsNode = findNode(CATEGORY_NAME_INDEXES.fixed_assets);
  const currentAssetsNode = findNode(CATEGORY_NAME_INDEXES.current_assets);
  const nonCurrentAssetsNode = findNode(CATEGORY_NAME_INDEXES.non_current_assets);
  const currentLiabilitiesNode = findNode(CATEGORY_NAME_INDEXES.current_liabilities);
  const nonCurrentLiabilitiesNode = findNode(CATEGORY_NAME_INDEXES.non_current_liabilities);
  const equityNode = findNode(CATEGORY_NAME_INDEXES.equity);

  if (startingBalanceNode) {
    layout.push({
      category: 'starting_balance' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [startingBalanceNode],
      totalOptions: {
        padding: true,
        forceRowSize: SIZES.MEDIUM,
        disableLargeSize: true,
        hidePeriodToDateDifference: true
      },
      adjustTreeBeforeRender: forceNoChildren
    });
  }
  [
    { node: cashInNode, category: 'cash_in' },
    { node: cashOutNode, category: 'cash_out' },
    { node: netCashFlowNode, category: 'net_cash_flow' }
  ].forEach(({ node, category }) => {
    if (node) {
      layout.push({
        category: category as CATEGORY_NAME_INDEXES,
        datasource,
        displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
        nodes: [node],
        totalOptions: {
          startingLevel: 1
        }
      });
    }
  });
  if (closingBalanceNode) {
    layout.push({
      category: 'closing_balance' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [closingBalanceNode],
      totalOptions: {
        padding: true,
        forceRowSize: SIZES.MEDIUM,
        disableLargeSize: true,
        hidePeriodToDateDifference: true
      },
      adjustTreeBeforeRender: forceNoChildren
    });
  }

  if (cashFromBusinessActivitiesNode) {
    layout.push({
      category: 'cash_from_business_activities' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [cashFromBusinessActivitiesNode],
      totalOptions: {
        padding: true,
        forceRowSize: SIZES.MEDIUM,
        disableLargeSize: true,
        hidePeriodToDateDifference: true
      },
      adjustTreeBeforeRender: forceNoChildren
    });
    [
      { node: incomeNode, category: CATEGORY_NAME_INDEXES.income },
      { node: costOfSalesNode, category: CATEGORY_NAME_INDEXES.cost_of_sales },
      { node: expensesNode, category: CATEGORY_NAME_INDEXES.expenses },
      { node: otherIncomeNode, category: CATEGORY_NAME_INDEXES.other_income },
      { node: otherExpensesNode, category: CATEGORY_NAME_INDEXES.other_expenses }
    ].forEach(({ node, category }) => {
      if (node) {
        layout.push({
          category: category as CATEGORY_NAME_INDEXES,
          datasource,
          displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
          nodes: [node],
          totalOptions: {
            startingLevel: 1
          },
          options: {
            startingLevel: 1
          }
        });
      }
    });
  }

  if (cashFromFinancingInvestingNode) {
    layout.push({
      category: 'cash_from_business_activities' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [cashFromFinancingInvestingNode],
      totalOptions: {
        padding: true,
        forceRowSize: SIZES.MEDIUM,
        disableLargeSize: true,
        hidePeriodToDateDifference: true
      },
      adjustTreeBeforeRender: forceNoChildren
    });
    [
      { node: fixedAssetsNode, category: CATEGORY_NAME_INDEXES.fixed_assets },
      { node: currentAssetsNode, category: CATEGORY_NAME_INDEXES.current_assets },
      { node: nonCurrentAssetsNode, category: CATEGORY_NAME_INDEXES.non_current_assets },
      { node: currentLiabilitiesNode, category: CATEGORY_NAME_INDEXES.current_liabilities },
      { node: nonCurrentLiabilitiesNode, category: CATEGORY_NAME_INDEXES.non_current_liabilities },
      { node: equityNode, category: CATEGORY_NAME_INDEXES.equity }
    ].forEach(({ node, category }) => {
      if (node) {
        layout.push({
          category: category as CATEGORY_NAME_INDEXES,
          datasource,
          displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
          nodes: [node],
          totalOptions: {
            startingLevel: 1
          },
          options: {
            startingLevel: 1
          }
        });
      }
    });
  }

  return layout;
};

export const CashFlowStatementV2 = () => {
  const accountNameMap = useOrganisationAccountNames();
  const { forecast, forecastReportV2: forecastReport, isLoading, totalCashPredictRow } = usePredictionsInfoContext();

  const { dataGridRows } = useUKForecastGrid(forecast, forecastReport, accountNameMap, getLayout);

  if (!forecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={forecastReport?.data.cash_flow_columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
        isCashFlow
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
